import { useEffect, useRef } from "react";
import { ENVIRONMENT, getEnvironment } from "../../../shared/utils/EnvConfig";
import { endChat, isChatEnabled, isOnlyForProd } from "../Utils/chat";

type ChatLauncher = "chat" | "wifiExtender";

interface ChatUser {
  isLoggedIn: boolean;
  custId?: string | null;
  accessToken?: string | null;
}

interface ChatIntegrationOptions {
  isLoggedIn: boolean;
  custId?: string | null;
  accessToken?: string | null;
}

const ensureChatParameters = () => {
  window.chatParameters = window.chatParameters || {
    debug: false,
    jwthost: "",
    ccaiphost: "",
    companyID: "",
    menuKey: "",
  };
};

const setChatEnvironmentData = (env: ReturnType<typeof getEnvironment>) => {
  ensureChatParameters();
  window.chatParameters.jwthost = env.chatstartscriptChatparamJwthost;
  window.chatParameters.ccaiphost = env.chatstartscriptChatparamCCaiphost;
  window.chatParameters.companyID = env.chatstartscriptChatparamCompanyID;
  window.chatParameters.menuKey = env.chatstartscriptChatparamMenuKey;
};

const setChatAuthData = (custId?: string | null, accessToken?: string | null) => {
  ensureChatParameters();
  window.chatParameters.customData = {
    ...window.chatParameters.customData,
    k8: { id: custId || "", auth: accessToken || "" },
  };
};

export const useChatIntegration = ({ isLoggedIn, custId, accessToken }: ChatIntegrationOptions) => {
  const chatUserRef = useRef<ChatUser>({ isLoggedIn });

  useEffect(() => {
    const previous = chatUserRef.current;
    // Keep the last known customer id while account details reload after login.
    const currentCustId = isLoggedIn ? custId || previous.custId : null;
    const currentAccessToken = isLoggedIn ? accessToken || previous.accessToken : null;

    // A BAN switch keeps the same login session; end chat only on an actual logout.
    if (previous.isLoggedIn && !isLoggedIn) {
      endChat();
    }

    chatUserRef.current = {
      isLoggedIn,
      custId: currentCustId,
      accessToken: currentAccessToken,
    };
    setChatAuthData(currentCustId, currentAccessToken);
  }, [isLoggedIn, custId, accessToken]);

  useEffect(() => {
    const isProductionHost = isOnlyForProd();
    const env = isProductionHost
      ? getEnvironment()
      : process.env.REACT_APP_ENV === "uat"
        ? ENVIRONMENT.uat
        : ENVIRONMENT.qa;
    let startupScript: HTMLScriptElement | null = null;
    let controlScript: HTMLScriptElement | null = null;
    let chatScriptsPromise: Promise<void> | null = null;
    let pendingLauncher: ChatLauncher | null = null;
    let chatReady = false;
    let isActive = true;

    const removeScripts = () => {
      if (startupScript?.parentNode) startupScript.parentNode.removeChild(startupScript);
      if (controlScript?.parentNode) controlScript.parentNode.removeChild(controlScript);
      startupScript = null;
      controlScript = null;
    };

    const inject = () => {
      if (chatScriptsPromise) return chatScriptsPromise;

      setChatEnvironmentData(env);
      setChatAuthData(chatUserRef.current.custId, chatUserRef.current.accessToken);
      window.endChat = endChat;

      const loadScript = (element: HTMLScriptElement, src: string) =>
        new Promise<void>((resolve, reject) => {
          element.src = src;
          element.async = true;
          element.onload = () => resolve();
          element.onerror = () => reject(new Error(`Chat script failed to load: ${src}`));
          document.head.appendChild(element);
        });

      startupScript = document.createElement("script");
      controlScript = document.createElement("script");
      // The controller reads chatParameters immediately and creates the launcher
      // when it evaluates, so it must load after the startup script initializes it.
      chatScriptsPromise = loadScript(startupScript, env.chatstartscriptSrc)
        .then(() => {
          setChatEnvironmentData(env);
          setChatAuthData(chatUserRef.current.custId, chatUserRef.current.accessToken);
          return loadScript(controlScript as HTMLScriptElement, env.chatControlscriptSrc);
        })
        .then(() => {
          if (!isActive) return;
          // The vendor startup script replaces window.chatParameters with its defaults.
          // Restore the selected environment after it loads so the chatbox receives the
          // correct hosts and company/menu configuration.
          setChatEnvironmentData(env);
          setChatAuthData(chatUserRef.current.custId, chatUserRef.current.accessToken);
          const chatDev = document.getElementById("chatLauncher");
          if (chatDev) chatDev.setAttribute("aria-label", "chat now window");
          chatReady = true;

          const requestedLauncher = pendingLauncher;
          pendingLauncher = null;
          if (requestedLauncher) {
            const launch = requestedLauncher === "wifiExtender"
              ? window.launchAddWifiExtenderChat
              : window.launchChat;
            if (typeof launch === "function") launch();
          }
        })
        .catch((error) => {
          if (isActive) console.error("Chat scripts could not be loaded", error);
          chatReady = false;
          removeScripts();
          chatScriptsPromise = null;
        });

      return chatScriptsPromise;
    };

    const onChatLaunchRequest = (event: Event) => {
      const requestedLauncher = (event as CustomEvent<ChatLauncher>).detail;
      if (requestedLauncher !== "chat" && requestedLauncher !== "wifiExtender") return;

      const launch = requestedLauncher === "wifiExtender"
        ? window.launchAddWifiExtenderChat
        : window.launchChat;
      if (chatReady && typeof launch === "function") {
        launch();
        return;
      }

      pendingLauncher = requestedLauncher;
      void inject();
    };

    // Load vendor scripts on production and GCP account hosts only.
    if (!isChatEnabled()) {
      return () => {
        isActive = false;
      };
    }

    window.addEventListener("chat-launch-request", onChatLaunchRequest);

    const idle = (window as any).requestIdleCallback as
      | ((callback: () => void, options?: { timeout: number }) => number)
      | undefined;
    const handle = idle
      ? idle(() => { void inject(); }, { timeout: 3000 })
      : window.setTimeout(() => { void inject(); }, 1500);

    return () => {
      isActive = false;
      const cancelIdle = (window as any).cancelIdleCallback as
        | ((id: number) => void)
        | undefined;
      if (idle && cancelIdle) cancelIdle(handle);
      else window.clearTimeout(handle);
      window.removeEventListener("chat-launch-request", onChatLaunchRequest);
      removeScripts();
    };
  }, []);
};
